import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { State } from 'src/app/components/designer/models/state';
import { Step } from 'src/app/components/designer/models/step';
import { TimerTriggerData } from 'src/app/components/designer/models/timer-trigger-data';
import { Workflow } from 'src/app/components/designer/models/workflow';
import { Attribute } from 'src/app/models/attribute';
import { DateReferences } from 'src/app/models/enums/date-references';
import { DelayTypes } from 'src/app/models/enums/delay-types';
import { PropertyType } from 'src/app/models/enums/property-type';
import { TimeUnits } from 'src/app/models/enums/time-units';
import { FormMode } from '../../common/form-mode';
import { ContextualMenuEvent } from '../contextual-menu-event';
import { NeedAttributes } from '../need-attributes';
import { NeedSteps } from '../need-steps';
import { NeedWorkflow } from '../need-workflow';

@Component({
    selector: 'app-workflow-trigger-timer-data',
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, FaIconComponent, CommonModule, MatTooltipModule],
    templateUrl: './workflow-trigger-timer-data.component.html',
    styleUrl: './workflow-trigger-timer-data.component.scss',
})
export class WorkflowTriggerTimerDataComponent implements OnInit, NeedSteps, NeedAttributes, NeedWorkflow {
    /**
     * Liste des icones utilisés dans le template.
     */
    public faCircleInfo = faCircleInfo;

    /**
     * Liste des Enums utilisées dans le template.
     */
    public propertyType: typeof PropertyType = PropertyType;
    public FormMode = FormMode;
    public DateReferences = DateReferences;
    public TimeUnits = TimeUnits;
    public DelayTypes = DelayTypes;

    /**
     * Liste d'éléments utilisés par les liste déroulantes du template.
     */
    public DateReferencesList: { value: DateReferences; name: string }[] = [
        { value: DateReferences.FolderCreation, name: 'Création du dossier' },
        { value: DateReferences.LastFolderUpdate, name: 'Dernière modification du dossier' },
        { value: DateReferences.WorkflowStepStart, name: "Début de l'étape courante" },
        { value: DateReferences.WorkflowStateStart, name: "Début de l'état courant" },
        { value: DateReferences.DateAttribute, name: 'Attribut de type date' },
    ];

    public TimeUnitsList: { value: TimeUnits; name: string }[] = [
        { value: TimeUnits.Minuts, name: 'Minute' },
        { value: TimeUnits.Hour, name: 'Heure' },
        { value: TimeUnits.Day, name: 'Jour' },
        { value: TimeUnits.Month, name: 'Mois' },
    ];

    public DelayTypesList: { value: DelayTypes; name: string }[] = [
        { value: DelayTypes.FixedValue, name: 'Valeur fixe' },
        { value: DelayTypes.AttributeValue, name: "Valeur d'un attribut du dossier" },
        { value: DelayTypes.WorkflowMaxDuration, name: 'Durée maxi du workflow' },
        { value: DelayTypes.WorkflowStepMaxDuration, name: "Durée maxi de l'étape de workflow" },
    ];

    /**
     * Données affichées par le composant dynamique.
     */
    public _data: TimerTriggerData;

    /**
     * Spécifie les données affichées par le composant dynamique.
     */
    set data(value: string | TimerTriggerData) {
        if (typeof value === 'string') {
            this._data = JSON.parse(value);
        } else {
            this._data = value as TimerTriggerData;
        }

        this.fillForm();
    }

    /**
     * Obtient les données affichées par le composant dynamique.
     */
    get data(): TimerTriggerData {
        this.fillData();
        return this._data;
    }

    /**
     * Mode d'affichage du formulaire.
     */
    mode: FormMode;

    /**
     * Source du menu contextuel.
     */
    contextualMenuSource: Subject<ContextualMenuEvent> = new Subject<ContextualMenuEvent>();

    /**
     * Spécifie la liste des attributs de la vue.
     */
    set attributes(value: Attribute[]) {
        this._dateAttributes = value
            .filter((x) => x.type === PropertyType.Date || x.type === PropertyType.DateAndTime)
            .sort((a, b) => {
                return a.header < b.header ? -1 : 1;
            });

        this._intAttributes = value
            .filter((x) => x.type === PropertyType.Long)
            .sort((a, b) => {
                return a.header < b.header ? -1 : 1;
            });

        console.log('WorkflowTriggerTimerDataComponent - dateAttributes', value);
        this.fillForm();
    }

    /**
     * Liste des attributs de type Date.
     */
    public _dateAttributes: Attribute[];

    /**
     * Liste des attributs de type Entier.
     */
    public _intAttributes: Attribute[];

    /**
     * Liste des étapes du workflow.
     */
    public _steps: Step[];

    /**
     * Spécifie la Liste des étapes du workflow.
     */
    set steps(value: Step[]) {
        this._steps = value;
        this.fillForm();
    }

    /**
     * Instance du workflow.
     */
    public _workflow: Workflow;

    /**
     * Spécifie l'instance du workflow.
     */
    set workflow(value: Workflow) {
        this._workflow = value;
    }

    /**
     * Liste des états de l'étape selectionnée.
     */
    states: State[];

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Attribut selectionné par l'utilisateur
     */
    public selectedAttribute: Attribute = null;

    /**
     * Refrence selectionnée par l'utilisateur.
     */
    public selectedReference: DateReferences = null;

    /**
     * Type de délai selectionné par l'utilisateur.
     */
    public selectedDelayType: DelayTypes = null;

    /**
     * Constructeur.
     */
    constructor() {}

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.dataForm = new UntypedFormGroup({
            reference: new UntypedFormControl('', [Validators.required]),
            delay: new UntypedFormControl(''),
            unit: new UntypedFormControl('', [Validators.required]),
            dateAttribute: new UntypedFormControl(''),
            delayAttribute: new UntypedFormControl(''),
            step: new UntypedFormControl(''),
            state: new UntypedFormControl(''),
            delayType: new UntypedFormControl('', [Validators.required]),
        });

        this.fillForm();
    }

    /**
     * Indique si le formulaire est valide.
     */
    public isValid(): boolean {
        return this.dataForm.valid;
    }

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    public get dirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Appellée lorsque l'utilisateur selectionne une reference.
     */
    public onReferenceChanged(event: MatSelectChange) {
        this.dataForm.get('dateAttribute').setValue('');
        this.dataForm.get('step').setValue('');
        this.dataForm.get('state').setValue('');
        this.dataForm.get('delayType').setValue('');

        this.displayFormForReference(event.value);
    }

    /**
     * Appellée lorsque l'utilisateur selectionne un type de délai.
     */
    public onDelayTypeChanged(event: MatSelectChange) {
        this.dataForm.get('delay').setValue('');
        this.dataForm.get('delayAttribute').setValue('');

        this.displayFormForDelayType(event.value);
    }

    /**
     * Appellée lorsque l'utilisateur selectionne une étape.
     */
    public onStepChanged(event: MatSelectChange) {
        this.dataForm.get('state').setValue('');

        this.displayFormForStep(event.value);
    }

    /**
     * Remplit le formulaire avec les données.
     */
    private fillForm() {
        if (this.dataForm) {
            if (this._data && this._dateAttributes) {
                this.dataForm.get('reference').setValue(this._data.dateReference);
                this.displayFormForReference(this._data.dateReference);
                this.dataForm.get('delay').setValue(this._data.delay);
                this.dataForm.get('delayType').setValue(this._data.delayType);
                this.displayFormForDelayType(this._data.delayType);
                this.dataForm.get('unit').setValue(this._data.unit);
                this.dataForm.get('dateAttribute').setValue(this._data.dateAttributeId);
                this.dataForm.get('delayAttribute').setValue(this._data.delayAttributeId);
                this.dataForm.get('step').setValue(this._data.stepId);
                this.displayFormForStep(this._data.stepId);
                this.dataForm.get('state').setValue(this._data.stateId);

                // if (dataObject.stateId) {
                //     if (this._stateSets) {
                //         this.states = this._stateSets.find((x) => x.id == dataObject.stepId).stateSetItems;
                //         this.dataForm.get('state').setValue(dataObject.stateId);
                //     }
                // }
            } else {
                this.dataForm.get('delay').setValue('');
            }
        }
    }

    /**
     * Met à jour les données à partir du formulaire.
     */
    private fillData() {
        let updatedData: TimerTriggerData = new TimerTriggerData();
        updatedData.dateReference = this.dataForm.get('reference').value;

        updatedData.delayType = this.dataForm.get('delayType').value;
        updatedData.unit = this.dataForm.get('unit').value;
        updatedData.dateAttributeId = parseInt(this.dataForm.get('dateAttribute').value);
        updatedData.delayAttributeId = parseInt(this.dataForm.get('delayAttribute').value);
        updatedData.stepId = parseInt(this.dataForm.get('step').value);
        updatedData.stateId = parseInt(this.dataForm.get('state').value);

        if (updatedData.delayType === DelayTypes.WorkflowMaxDuration) {
            updatedData.delay = this._workflow.maxDuration;
        } else if (updatedData.delayType === DelayTypes.WorkflowStepMaxDuration) {
            let step = this._workflow.steps.find((x) => x.id === updatedData.stepId);
            updatedData.delay = step.data.maxDuration;
        } else {
            updatedData.delay = parseInt(this.dataForm.get('delay').value);
        }

        this._data = updatedData;
    }

    /**
     * Affiche le formulaire adapté pour l'étape spécifiée.
     * @param stepId : Id de l'atepe.
     */
    private displayFormForStep(stepId: number) {
        if (stepId) {
            let selectedStep = this._workflow.steps.find((x) => x.id == stepId);
            this.states = selectedStep.data.states;
        } else {
            this.states = [];
        }
    }

    /**
     * Affiche le formulaire adapté pour la reference spécifiée.
     * @param selectedReference : Date de reference.
     */
    private displayFormForReference(selectedReference: DateReferences) {
        this.selectedReference = selectedReference;

        switch (this.selectedReference) {
            case DateReferences.DateAttribute:
                this.dataForm.get('dateAttribute').setValidators([Validators.required]);
                this.dataForm.get('step').clearValidators();
                this.dataForm.get('state').clearValidators();
                break;
            case DateReferences.WorkflowStepStart:
                this.dataForm.get('dateAttribute').clearValidators();
                this.dataForm.get('step').setValidators([Validators.required]);
                this.dataForm.get('state').clearValidators();
                break;
            case DateReferences.WorkflowStateStart:
                this.dataForm.get('dateAttribute').clearValidators();
                this.dataForm.get('step').setValidators([Validators.required]);
                this.dataForm.get('state').setValidators([Validators.required]);
                break;
            default:
                this.dataForm.get('dateAttribute').setValidators([]);
                this.dataForm.get('step').clearValidators();
                this.dataForm.get('state').clearValidators();
                break;
        }

        this.dataForm.get('dateAttribute').updateValueAndValidity();
        this.dataForm.get('step').updateValueAndValidity();
        this.dataForm.get('state').updateValueAndValidity();
    }

    /**
     * Affiche le formulaire adapté pour le type de délai spécifié
     * @param selectedDelayType : type de délai.
     */
    private displayFormForDelayType(selectedDelayType: DelayTypes) {
        this.selectedDelayType = selectedDelayType;

        switch (this.selectedDelayType) {
            case DelayTypes.FixedValue:
                this.dataForm.get('delay').setValidators([Validators.required]);
                this.dataForm.get('delayAttribute').clearValidators();
                break;
            case DelayTypes.AttributeValue:
                this.dataForm.get('delay').clearValidators();
                this.dataForm.get('delayAttribute').setValidators([Validators.required]);
                break;
            default:
                this.dataForm.get('delay').clearValidators();
                this.dataForm.get('delayAttribute').setValidators([]);
                break;
        }

        this.dataForm.get('delay').updateValueAndValidity();
        this.dataForm.get('delayAttribute').updateValueAndValidity();
    }
}
